import { useMutation } from 'convex/react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { SUPPORTED_LOCALES } from '../../i18n/i18n';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { StepIndicator } from './step-indicator';

interface LanguageStepProps {
  onNext: () => void;
  currentStep: number;
  totalSteps: number;
}

export function LanguageStep({
  onNext,
  currentStep,
  totalSteps,
}: LanguageStepProps) {
  const { t, i18n } = useTranslation();
  const updateSettings = useMutation(api.userSettings.updateSettings);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleLanguageSelect = (lang: string) => {
    setSelectedLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const handleContinue = async () => {
    setIsSubmitting(true);
    try {
      await updateSettings({
        language: selectedLanguage,
        homepageFilterLanguage: [selectedLanguage],
      });
      setHasSubmitted(true);
      onNext();
    } catch (error) {
      console.error('Failed to update language', error);
      setHasSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex h-full w-full max-w-xl flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">
          {t('onboarding:languageTitle')}
        </h1>
        <p className="text-muted-foreground">
          {t('onboarding:languageDescription')}
        </p>
      </div>

      <div className="flex grow flex-col gap-4 overflow-y-auto">
        {SUPPORTED_LOCALES.map((locale) => (
          <button
            key={locale}
            onClick={() => handleLanguageSelect(locale)}
            className={cn(
              'flex cursor-pointer items-center justify-between rounded-xl border bg-gray-100 px-5 py-4 text-left transition-all',
              selectedLanguage === locale
                ? 'border-primary/30 bg-primary/10'
                : 'hover:bg-gray-200/70',
            )}>
            <span className="text-lg font-medium">
              {t(`home:languages.${locale}`)}
            </span>
            {selectedLanguage === locale && (
              <CheckIcon className="text-primary h-5 w-5" />
            )}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        <Button
          className="w-full"
          onClick={handleContinue}
          loading={isSubmitting || hasSubmitted}
          disabled={isSubmitting || hasSubmitted}>
          {t('login:continue')}
        </Button>
      </div>
    </motion.div>
  );
}
